/**
 * ExpenseDashboardPage Component
 * Portfolio-wide expense dashboard with summary cards and filters.
 * Mobile-first: card list on mobile, table on desktop.
 * Route: /expenses
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTrackFeature } from '@resida/analytics';
import { expensesApi, expenseCategoriesApi, listProperties, listUnits } from '../lib/api';
import type { Expense, ExpenseCategory, CreateExpenseBody, UpdateExpenseBody } from '../types/expense.types';
import type { PropertyWithStats, UnitWithRelations } from '../lib/api';
import { ExpenseForm } from '../components/expenses/ExpenseForm';
import { ExpenseUploadDropzone } from '../components/expenses/ExpenseUploadDropzone';
import { Modal } from '../components/Modal';
import { formatAmount, formatExpenseLocation } from '../utils/expense.utils';
import { formatCalendarDate, todayInTimezone, asCalendarDateString, formatUnitLabel } from '@resida/shared';
import { getDatePresetRange } from '../utils/date-preset.utils';
import { PageHeader, StatRow, StatCard } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';

export function ExpenseDashboardPage() {
  useTrackFeature('expenses', 'landlord');

  // Portfolio-level page — property state is not available in the Property type.
  // Use Eastern as a reasonable default; expense calendar dates are rarely sensitive to TZ at this level.
  const propertyTimezone = 'America/New_York';
  const last30Range = getDatePresetRange('last30', propertyTimezone);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [reviewCount, setReviewCount] = useState<number | null>(null);

  // Delete confirm
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Add expense
  type AddExpenseMode = 'none' | 'choose' | 'manual' | 'upload';
  const [addExpenseMode, setAddExpenseMode] = useState<AddExpenseMode>('none');
  const [addPropertyId, setAddPropertyId] = useState('');
  const [addUnitId, setAddUnitId] = useState('');
  const [addUnits, setAddUnits] = useState<UnitWithRelations[]>([]);
  const [addUnitsLoading, setAddUnitsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Filters
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [datePreset, setDatePreset] = useState('last30');
  const [startDate, setStartDate] = useState(last30Range.start);
  const [endDate, setEndDate] = useState(last30Range.end);

  useEffect(() => {
    async function loadBootstrapData() {
      setPropertiesLoading(true);
      try {
        const [propertiesResult, categoriesResult] = await Promise.all([
          listProperties(),
          expenseCategoriesApi.list(),
        ]);
        setProperties(propertiesResult.properties);
        setCategories(categoriesResult.categories);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        setPropertiesLoading(false);
      }
    }
    loadBootstrapData();
  }, []);

  // Fetch review queue count for the banner
  useEffect(() => {
    expensesApi.getReviewQueue()
      .then((res) => setReviewCount(res.expenses.length))
      .catch(() => setReviewCount(null));
  }, []);

  useEffect(() => {
    if (!addPropertyId) {
      setAddUnits([]);
      return;
    }
    setAddUnitsLoading(true);
    setAddUnitId('');
    listUnits({ propertyId: addPropertyId })
      .then((res) => setAddUnits(res.units))
      .catch(() => setAddUnits([]))
      .finally(() => setAddUnitsLoading(false));
  }, [addPropertyId]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await expensesApi.list({
        propertyId: selectedPropertyId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        categoryId: selectedCategoryId || undefined,
        sourceType: undefined,
        status: 'CONFIRMED',
      });
      setExpenses(result.expenses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId, startDate, endDate, selectedCategoryId]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const openAddExpense = () => {
    setAddPropertyId(selectedPropertyId);
    setAddExpenseMode('choose');
  };

  const closeAddExpense = () => {
    setAddExpenseMode('none');
    setAddPropertyId('');
    setAddUnitId('');
    setAddUnits([]);
  };

  const handleAddCreateSubmit = async (values: CreateExpenseBody | UpdateExpenseBody) => {
    setIsCreating(true);
    try {
      await expensesApi.create(values as CreateExpenseBody);
      closeAddExpense();
      await loadExpenses();
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddUploadSuccess = () => {
    closeAddExpense();
    loadExpenses();
  };

  const propertyToday = todayInTimezone(propertyTimezone);
  const currentYear = parseInt(propertyToday.substring(0, 4));
  const currentYearMonth = propertyToday.substring(0, 7); // 'YYYY-MM'

  const totalYear = expenses.reduce((sum, e) => sum + e.amount, 0);
  const totalMonth = expenses
    .filter((e) => (e.expenseDate ?? '').startsWith(currentYearMonth))
    .reduce((sum, e) => sum + e.amount, 0);

  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  if (propertiesLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-4 grid grid-cols-2 gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
        <div className="px-[var(--page-padding-x)] mt-4 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <ExpensesLayout>
    <div data-testid="expense-dashboard">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Expenses"
          action={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/expenses/recurring">Recurring</Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/expense-reports" data-testid="expense-dashboard-year-end-link">Report</Link>
              </Button>
              <Button
                size="sm"
                onClick={openAddExpense}
                data-testid="expense-dashboard-add-button"
                aria-label="Add expense"
              >
                + Add Expense
              </Button>
            </div>
          }
        />
      </div>

      {/* Help block */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <HelpBlock
          summary="This is your confirmed expense history. Track property costs here for tax reporting and financial oversight."
          details={"Expenses can arrive three ways:\n\n1. Email \u2014 Forward receipts to your expense email address and AI extracts the details automatically.\n2. Upload \u2014 Upload receipt images or PDFs directly.\n3. Manual \u2014 Type in expense details yourself.\n\nEmail and upload expenses are processed by AI, which may send them to the Confirm Expenses tab for your review before they appear here. Only confirmed expenses show up in this list and in your reports."}
        />
      </div>

      {/* Review Queue Banner */}
      {reviewCount !== null && reviewCount > 0 && (
        <div
          className="mx-[var(--page-padding-x)] mb-4 flex items-center justify-between px-3 py-2.5 bg-warning/5 border border-warning/30 rounded-lg"
          data-testid="review-queue-banner"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-warning">
              {reviewCount} expense{reviewCount !== 1 ? 's' : ''} need review
            </span>
            <HelpTooltip text="These expenses were processed by AI but need your confirmation before they count toward your totals and reports. Head to the Confirm Expenses tab to review them." />
          </div>
          <Link
            to="/expenses/confirm"
            className="text-sm font-semibold text-warning hover:underline"
            data-testid="review-queue-banner-link"
            aria-label={`Review ${reviewCount} pending expense${reviewCount !== 1 ? 's' : ''}`}
          >
            Review &rarr;
          </Link>
        </div>
      )}

      {/* Summary Stats */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <StatRow>
          <StatCard
            label={`Total ${currentYear}`}
            value={formatAmount(totalYear)}
            data-testid="expense-dashboard-total-year"
          />
          <StatCard
            label="This Month"
            value={formatAmount(totalMonth)}
            data-testid="expense-dashboard-total-month"
          />
          <StatCard
            label="Count"
            value={String(expenses.length)}
            data-testid="expense-dashboard-count"
          />
        </StatRow>
      </div>

      {/* Filters */}
      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 items-end">
          <div className="col-span-2 sm:flex-1 sm:min-w-[180px]">
            <label htmlFor="dashboard-property-filter" className="block text-xs font-medium text-muted-foreground mb-1">
              Property
            </label>
            <select
              id="dashboard-property-filter"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              data-testid="expense-dashboard-filter-property"
              aria-label="Filter by property"
            >
              <option value="">All Properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="sm:flex-1 sm:min-w-[150px]">
            <label htmlFor="dashboard-category-filter" className="block text-xs font-medium text-muted-foreground mb-1">
              Category
            </label>
            <select
              id="dashboard-category-filter"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              data-testid="expense-dashboard-filter-category"
              aria-label="Filter by category"
            >
              <option value="">All</option>
              {sortedCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="sm:flex-1 sm:min-w-[120px]">
            <label htmlFor="dashboard-date-preset" className="block text-xs font-medium text-muted-foreground mb-1">
              Date Range
            </label>
            <select
              id="dashboard-date-preset"
              value={datePreset}
              onChange={(e) => {
                const preset = e.target.value;
                setDatePreset(preset);
                if (preset !== 'custom') {
                  const range = getDatePresetRange(preset, propertyTimezone);
                  setStartDate(range.start);
                  setEndDate(range.end);
                }
              }}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              data-testid="expense-dashboard-filter-date-preset"
              aria-label="Select date range preset"
            >
              <option value="last30">Last 30 Days</option>
              <option value="last60">Last 60 Days</option>
              <option value="last90">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
              <option value="lastYear">Last Year</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {datePreset === 'custom' && (
            <>
              <div className="sm:flex-1 sm:min-w-[130px]">
                <label htmlFor="dashboard-start-date" className="block text-xs font-medium text-muted-foreground mb-1">
                  From
                </label>
                <input
                  id="dashboard-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                  data-testid="expense-dashboard-filter-start"
                  aria-label="Filter start date"
                />
              </div>
              <div className="sm:flex-1 sm:min-w-[130px]">
                <label htmlFor="dashboard-end-date" className="block text-xs font-medium text-muted-foreground mb-1">
                  To
                </label>
                <input
                  id="dashboard-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                  data-testid="expense-dashboard-filter-end"
                  aria-label="Filter end date"
                />
              </div>
            </>
          )}

          <div className="col-span-2 sm:col-span-1">
            <Button
              onClick={loadExpenses}
              disabled={loading}
              data-testid="expense-dashboard-filter-apply"
              aria-label="Apply filters"
              className="w-full sm:w-auto"
            >
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div
          role="alert"
          className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-lg text-sm text-destructive flex items-center gap-3"
        >
          <span className="flex-1">{error}</span>
          <button type="button" className="text-sm text-destructive hover:underline" onClick={loadExpenses}>
            Retry
          </button>
        </div>
      )}

      {deleteError && (
        <div role="alert" className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-lg text-sm text-destructive">
          {deleteError}
        </div>
      )}

      {/* Expense List */}
      <div data-testid="expense-dashboard-table">
          {loading ? (
            <div className="px-[var(--page-padding-x)] space-y-2" aria-label="Loading expenses" aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : expenses.length === 0 ? (
            <div className="px-[var(--page-padding-x)] py-10 text-center text-muted-foreground flex flex-col items-center gap-3">
              <p>No expenses found for the selected filters.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedPropertyId('');
                  setSelectedCategoryId('');
                  setDatePreset('last30');
                  const range = getDatePresetRange('last30', propertyTimezone);
                  setStartDate(range.start);
                  setEndDate(range.end);
                }}
                data-testid="expense-dashboard-clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile: card list */}
              <div className="flex flex-col md:hidden">
                {expenses.map((expense) => (
                  <div
                    key={expense.id}
                    className="flex flex-col gap-1 px-[var(--page-padding-x)] py-3 border-b border-border last:border-b-0"
                    data-testid={`expense-dashboard-row-${expense.id}`}
                  >
                    {deletingId === expense.id ? (
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-foreground m-0">
                          Delete <strong>{expense.description}</strong> ({formatAmount(expense.amount)})?
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setDeletingId(null); setDeleteError(null); }}>
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              setDeleteError(null);
                              try {
                                await expensesApi.delete(expense.id);
                                setDeletingId(null);
                                await loadExpenses();
                              } catch (err) {
                                setDeleteError(err instanceof Error ? err.message : 'Failed to delete expense');
                              }
                            }}
                            data-testid={`expense-dashboard-delete-confirm-${expense.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2">
                          <Link
                            to={`/expenses/${expense.id}`}
                            className="text-sm font-medium text-foreground hover:text-primary truncate"
                            data-testid={`expense-dashboard-row-link-${expense.id}`}
                          >
                            {expense.description}
                          </Link>
                          <span className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                            {formatAmount(expense.amount)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {formatCalendarDate(asCalendarDateString(expense.expenseDate))}
                            {expense.vendorName && ` · ${expense.vendorName}`}
                          </span>
                          <button
                            type="button"
                            className="text-xs text-destructive hover:underline bg-transparent border-0 cursor-pointer p-0"
                            onClick={() => { setDeletingId(expense.id); setDeleteError(null); }}
                            data-testid={`expense-dashboard-delete-${expense.id}`}
                            aria-label={`Delete expense: ${expense.description}`}
                          >
                            Delete
                          </button>
                        </div>
                        {expense.irsCategoryId && (
                          <Badge variant="secondary" className="w-fit text-[10px] px-1.5 py-0 mt-0.5">
                            {categories.find((c) => c.id === expense.irsCategoryId)?.name ?? 'Uncategorized'}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {/* Mobile total */}
                <div className="flex items-center justify-between px-[var(--page-padding-x)] py-3 border-t-2 border-border">
                  <span className="text-xs font-semibold text-muted-foreground uppercase">Total</span>
                  <span className="text-sm font-bold text-foreground tabular-nums">{formatAmount(totalYear)}</span>
                </div>
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block overflow-x-auto px-[var(--page-padding-x)]">
                <table className="w-full border-collapse text-sm" aria-label="Expenses table">
                  <thead>
                    <tr>
                      {['Date', 'Description', 'Vendor', 'Category', 'Property', 'Amount', ''].map((col) => (
                        <th
                          key={col || 'actions'}
                          className={`px-3 py-2 border-b-2 border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col === 'Amount' ? 'text-right' : 'text-left'}`}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((expense) => (
                      <tr
                        key={expense.id}
                        className="border-b border-border"
                        data-testid={`expense-dashboard-row-${expense.id}`}
                      >
                        {deletingId === expense.id ? (
                          <td colSpan={7} className="px-3 py-3">
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-foreground flex-1">
                                Delete <strong>{expense.description}</strong> ({formatAmount(expense.amount)})? This cannot be undone.
                              </span>
                              <Button variant="outline" size="sm" onClick={() => { setDeletingId(null); setDeleteError(null); }}>
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={async () => {
                                  setDeleteError(null);
                                  try {
                                    await expensesApi.delete(expense.id);
                                    setDeletingId(null);
                                    await loadExpenses();
                                  } catch (err) {
                                    setDeleteError(err instanceof Error ? err.message : 'Failed to delete expense');
                                  }
                                }}
                                data-testid={`expense-dashboard-delete-confirm-${expense.id}`}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        ) : (
                          <>
                            <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                              {formatCalendarDate(asCalendarDateString(expense.expenseDate))}
                            </td>
                            <td className="px-3 py-3">
                              <Link
                                to={`/expenses/${expense.id}`}
                                className="text-primary font-medium hover:underline"
                                data-testid={`expense-dashboard-row-link-${expense.id}`}
                              >
                                {expense.description}
                              </Link>
                            </td>
                            <td className="px-3 py-3 text-muted-foreground">{expense.vendorName ?? '—'}</td>
                            <td className="px-3 py-3 text-muted-foreground">
                              {expense.irsCategoryId
                                ? (categories.find((c) => c.id === expense.irsCategoryId)?.name ?? expense.irsCategoryId)
                                : '—'}
                            </td>
                            <td className="px-3 py-3 text-muted-foreground">
                              {formatExpenseLocation(expense.propertyName, expense.unitNumber)}
                            </td>
                            <td className="px-3 py-3 text-right font-semibold text-foreground whitespace-nowrap tabular-nums">
                              {formatAmount(expense.amount)}
                            </td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setDeletingId(expense.id); setDeleteError(null); }}
                                data-testid={`expense-dashboard-delete-${expense.id}`}
                                aria-label={`Delete expense: ${expense.description}`}
                                className="text-destructive hover:text-destructive"
                              >
                                Delete
                              </Button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="px-3 py-3 border-t-2 border-border text-xs font-semibold text-muted-foreground">
                        Total
                      </td>
                      <td className="px-3 py-3 border-t-2 border-border text-right font-bold text-foreground whitespace-nowrap tabular-nums">
                        {formatAmount(totalYear)}
                      </td>
                      <td className="border-t-2 border-border" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
    </div>

      {/* Choose Method Modal */}
      <Modal
        isOpen={addExpenseMode === 'choose'}
        onClose={closeAddExpense}
        title="Add Expense"
      >
        <div className="flex flex-col gap-4">
          {/* Property selector */}
          <div>
            <label htmlFor="add-expense-property" className="block text-xs font-medium text-foreground mb-1">
              Property *
            </label>
            <select
              id="add-expense-property"
              value={addPropertyId}
              onChange={(e) => setAddPropertyId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
              data-testid="add-expense-property-select"
              aria-label="Select property"
            >
              <option value="">-- Select property --</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Unit selector */}
          {addPropertyId && (
            <div>
              <label htmlFor="add-expense-unit" className="block text-xs font-medium text-foreground mb-1">
                Unit <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <select
                id="add-expense-unit"
                value={addUnitId}
                onChange={(e) => setAddUnitId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background min-h-[44px]"
                disabled={addUnitsLoading}
                data-testid="add-expense-unit-select"
                aria-label="Select unit (optional)"
              >
                <option value="">-- All units / property-level --</option>
                {addUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {formatUnitLabel(u.unitNumber) ?? u.address1}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Method buttons */}
          <p className="m-0 text-sm text-muted-foreground">
            How would you like to add this expense?
          </p>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              className="rounded-lg p-4 border-2 border-border bg-background cursor-pointer text-left hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setAddExpenseMode('upload')}
              disabled={!addPropertyId}
              aria-label="Upload a receipt or invoice for AI processing"
              data-testid="add-expense-choose-upload"
            >
              <div className="text-base font-semibold text-foreground mb-1">
                Upload Receipt / Invoice
              </div>
              <div className="text-[13px] text-muted-foreground">
                Upload a PDF, image, or document. AI will extract the expense details automatically.
              </div>
            </button>
            <button
              type="button"
              className="rounded-lg p-4 border-2 border-border bg-background cursor-pointer text-left hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setAddExpenseMode('manual')}
              disabled={!addPropertyId}
              data-testid="add-expense-choose-manual"
              aria-label="Enter expense details manually"
            >
              <div className="text-base font-semibold text-foreground mb-1">
                Enter Manually
              </div>
              <div className="text-[13px] text-muted-foreground">
                Fill in the expense details yourself — amount, date, vendor, category, etc.
              </div>
            </button>
          </div>
        </div>
      </Modal>

      {/* Manual Entry Modal */}
      <Modal
        isOpen={addExpenseMode === 'manual'}
        onClose={closeAddExpense}
        title="Add Expense"
        width="large"
      >
        <ExpenseForm
          mode="create"
          propertyId={addPropertyId}
          unitId={addUnitId || undefined}
          categories={categories}
          onSubmit={handleAddCreateSubmit}
          onCancel={closeAddExpense}
          isLoading={isCreating}
        />
      </Modal>

      {/* Upload Receipt Modal */}
      <Modal
        isOpen={addExpenseMode === 'upload'}
        onClose={closeAddExpense}
        title="Upload Receipt or Invoice"
        width="large"
      >
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          Upload a PDF or image. We'll extract the expense details automatically.
        </p>
        <ExpenseUploadDropzone
          propertyId={addPropertyId}
          unitId={addUnitId || undefined}
          onSuccess={handleAddUploadSuccess}
        />
      </Modal>
    </ExpensesLayout>
  );
}

export default ExpenseDashboardPage;
